import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import {
  getRetentionPolicies,
  updateRetentionPolicy,
  getRetentionStats,
  runRetention,
  getRetentionRuns,
  getArchivedRecords,
  restoreArchivedRecord,
} from '../lib/api';
import {
  Archive,
  Warning,
  Play,
  ClockCounterClockwise,
  ArrowUUpLeft,
  Database,
  Shield,
  CheckCircle,
  FloppyDisk,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

function PolicyRow({ stat, onSave }) {
  const [archive, setArchive] = useState(stat.archive_after_days);
  const [del, setDel] = useState(stat.delete_after_days);
  const [enabled, setEnabled] = useState(stat.enabled);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setArchive(stat.archive_after_days);
    setDel(stat.delete_after_days);
    setEnabled(stat.enabled);
    setDirty(false);
  }, [stat]);

  const save = async () => {
    if (del < archive) {
      toast.error('delete_after_days must be >= archive_after_days');
      return;
    }
    setSaving(true);
    try {
      await onSave(stat.entity, {
        archive_after_days: parseInt(archive),
        delete_after_days: parseInt(del),
        enabled,
      });
      setDirty(false);
      toast.success(`Policy updated: ${stat.entity}`);
    } catch (e) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`policy-row-${stat.entity}`}>
      <td className="font-mono font-bold">{stat.entity}</td>
      <td className="text-xs text-muted-foreground font-mono">{stat.ts_field}</td>
      <td className="font-mono">{stat.active_count.toLocaleString()}</td>
      <td className="font-mono text-muted-foreground">{stat.archived_count.toLocaleString()}</td>
      <td>
        {stat.eligible_for_archive > 0 ? (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold uppercase">
            {stat.eligible_for_archive.toLocaleString()} ready
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
      <td>
        {stat.eligible_for_delete > 0 ? (
          <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-bold uppercase">
            {stat.eligible_for_delete.toLocaleString()} purge
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </td>
      <td>
        <Input
          type="number"
          min={0}
          value={archive}
          onChange={(e) => { setArchive(e.target.value); setDirty(true); }}
          className="w-20 h-8 font-mono text-sm"
          data-testid={`policy-archive-${stat.entity}`}
        />
      </td>
      <td>
        <Input
          type="number"
          min={0}
          value={del}
          onChange={(e) => { setDel(e.target.value); setDirty(true); }}
          className="w-24 h-8 font-mono text-sm"
          data-testid={`policy-delete-${stat.entity}`}
        />
      </td>
      <td>
        <button
          onClick={() => { setEnabled(!enabled); setDirty(true); }}
          data-testid={`policy-enabled-${stat.entity}`}
          className={`px-2 py-0.5 text-xs font-bold uppercase ${enabled ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-muted text-muted-foreground border border-border'}`}
        >
          {enabled ? 'ON' : 'OFF'}
        </button>
      </td>
      <td>
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || saving}
          onClick={save}
          data-testid={`policy-save-${stat.entity}`}
          className={dirty ? 'border-brand text-brand' : ''}
        >
          <FloppyDisk size={12} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </td>
    </tr>
  );
}

export default function Retention() {
  const [stats, setStats] = useState([]);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [browseEntity, setBrowseEntity] = useState(null);
  const [archiveData, setArchiveData] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, runsRes] = await Promise.all([getRetentionStats(), getRetentionRuns()]);
      setStats(statsRes.data);
      setRuns(runsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load retention data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRunNow = async () => {
    if (!window.confirm('Run archival job now? This will move stale records to archive and purge expired archives.')) return;
    setRunning(true);
    try {
      const res = await runRetention();
      toast.success(`Run complete: ${res.data.total_archived} archived, ${res.data.total_deleted_archive + res.data.total_deleted_main} purged`);
      fetchAll();
    } catch (e) {
      toast.error('Run failed');
    } finally {
      setRunning(false);
    }
  };

  const openBrowse = async (entity) => {
    setBrowseEntity(entity);
    setArchiveLoading(true);
    try {
      const res = await getArchivedRecords(entity, 0, 50);
      setArchiveData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleRestore = async (entity, id) => {
    if (!window.confirm('Restore this record back to the main collection?')) return;
    try {
      await restoreArchivedRecord(entity, id);
      toast.success('Record restored');
      openBrowse(entity);
      fetchAll();
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const totals = stats.reduce(
    (acc, s) => ({
      active: acc.active + s.active_count,
      archived: acc.archived + s.archived_count,
      eligibleArchive: acc.eligibleArchive + s.eligible_for_archive,
      eligibleDelete: acc.eligibleDelete + s.eligible_for_delete,
    }),
    { active: 0, archived: 0, eligibleArchive: 0, eligibleDelete: 0 }
  );

  return (
    <Layout>
      <div data-testid="retention-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Archive size={32} weight="fill" className="text-foreground" />
              Data Retention &amp; Archival
            </h1>
            <p className="text-muted-foreground">SOX-style 7-year retention · scheduler runs every 24h automatically</p>
          </div>
          <Button
            onClick={handleRunNow}
            disabled={running}
            data-testid="retention-run-now-btn"
            className="bg-brand hover:bg-brand/90"
          >
            <Play size={18} className="mr-2" weight="fill" />
            {running ? 'Running...' : 'Run Now'}
          </Button>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="overline">Active Records</p>
            <p className="text-3xl font-bold">{totals.active.toLocaleString()}</p>
          </div>
          <div className="bg-muted border border-border p-4">
            <p className="overline">Archived</p>
            <p className="text-3xl font-bold text-foreground">{totals.archived.toLocaleString()}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-4">
            <p className="overline text-amber-800">Ready for Archive</p>
            <p className="text-3xl font-bold text-amber-800">{totals.eligibleArchive.toLocaleString()}</p>
          </div>
          <div className="bg-red-50 border border-red-200 p-4">
            <p className="overline text-red-800">Eligible for Purge</p>
            <p className="text-3xl font-bold text-red-800">{totals.eligibleDelete.toLocaleString()}</p>
          </div>
        </div>

        <Tabs defaultValue="policies" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="policies" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              <Shield size={16} className="mr-2" /> Policies
            </TabsTrigger>
            <TabsTrigger value="archive" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              <Database size={16} className="mr-2" /> Archive Browser
            </TabsTrigger>
            <TabsTrigger value="runs" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">
              <ClockCounterClockwise size={16} className="mr-2" /> Run History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="policies" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Timestamp Field</th>
                    <th>Active</th>
                    <th>Archived</th>
                    <th>Ready Archive</th>
                    <th>Ready Purge</th>
                    <th>Archive after (days)</th>
                    <th>Delete after (days)</th>
                    <th>Enabled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : stats.map(s => (
                    <PolicyRow key={s.entity} stat={s} onSave={async (entity, data) => {
                      await updateRetentionPolicy(entity, data);
                      fetchAll();
                    }} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 bg-muted border-t border-border text-xs text-muted-foreground">
              <p><span className="font-bold uppercase tracking-widest">Scoring:</span> Records older than <span className="font-mono font-bold">archive_after_days</span> move to the <span className="font-mono">archive_&lt;entity&gt;</span> collection. Archives older than <span className="font-mono font-bold">delete_after_days</span> are permanently deleted. Default 2-year archive / 7-year retention for transactional entities, 30-day delete for login attempts.</p>
            </div>
          </TabsContent>

          <TabsContent value="archive" className="p-0 m-0">
            <div className="p-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {stats.map(s => (
                  <button
                    key={s.entity}
                    onClick={() => openBrowse(s.entity)}
                    data-testid={`browse-${s.entity}`}
                    className={`px-3 py-1.5 text-sm border font-mono ${browseEntity === s.entity ? 'bg-brand text-white border-brand' : 'bg-card border-border rounded-lg hover:bg-muted'}`}
                  >
                    {s.entity} <span className="opacity-70 ml-1">({s.archived_count})</span>
                  </button>
                ))}
              </div>

              {!browseEntity ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Database size={40} className="mx-auto mb-2 text-slate-300" />
                  <p>Select an entity above to browse its archived records</p>
                </div>
              ) : archiveLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading archived records...</div>
              ) : !archiveData || archiveData.items.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive size={40} className="mx-auto mb-2 text-slate-300" />
                  <p>No archived records for <span className="font-mono font-bold">{browseEntity}</span></p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Showing {archiveData.items.length} of {archiveData.total} archived records
                  </p>
                  <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-border">
                    <table className="w-full table-dense">
                      <thead className="sticky top-0 bg-card">
                        <tr>
                          <th>ID</th>
                          <th>Archived At</th>
                          <th>Preview</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archiveData.items.map(item => (
                          <tr key={item.id} data-testid={`archive-row-${item.id}`}>
                            <td className="font-mono text-xs text-muted-foreground">{item.id.slice(-8)}</td>
                            <td className="text-xs text-muted-foreground">
                              {item.archived_at ? new Date(item.archived_at).toLocaleString() : '-'}
                            </td>
                            <td className="text-xs text-foreground max-w-lg truncate font-mono">
                              {JSON.stringify(item).slice(0, 150)}...
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRestore(browseEntity, item.id)}
                                data-testid={`restore-${item.id}`}
                                className="border-brand text-brand"
                              >
                                <ArrowUUpLeft size={12} className="mr-1" /> Restore
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="runs" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Triggered By</th>
                    <th>Archived</th>
                    <th>Deleted (Main)</th>
                    <th>Deleted (Archive)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : runs.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12">
                      <ClockCounterClockwise size={40} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-muted-foreground">No retention runs yet. Click "Run Now" or wait for the scheduled 24h run.</p>
                    </td></tr>
                  ) : runs.map(r => (
                    <tr key={r.id} data-testid={`run-row-${r.id}`}>
                      <td className="text-sm">{r.started_at ? new Date(r.started_at).toLocaleString() : '-'}</td>
                      <td className="font-mono">{(r.duration_seconds || 0).toFixed(2)}s</td>
                      <td className="font-mono text-xs uppercase text-muted-foreground">{r.triggered_by}</td>
                      <td className="font-mono font-bold">{r.total_archived || 0}</td>
                      <td className="font-mono">{r.total_deleted_main || 0}</td>
                      <td className="font-mono">{r.total_deleted_archive || 0}</td>
                      <td>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold uppercase">
                          <CheckCircle size={12} weight="fill" /> Complete
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
